import React, { useEffect, useState } from 'react';
import { useDebounce, useCurrency } from '~Hooks';
import styled from '@emotion/styled';

import { useI18n } from '~Hooks';
import { SwipeView } from './Components/SwipeView';
import { ManualView } from './Components/ManualView';
import { QRScanView } from './Components/QRScanView';
import { Button } from '~Styles';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useGiftCardsContext } from '~Scenes/GiftCards/GiftCardsProvider';
import { RefreshGiftcard, RefreshGiftcardVariables } from './__queries__';
import { GIFT_CARD_BY_SHORTCODE } from '~Queries/gift-cards';
import { handyBreakpoint } from '~Styles/breakpoints';

const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 60;
  animation: fade-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const AbsoluteWrapper = styled.div<{
  top?: number;
}>`
  position: absolute;
  top: 200px;
  width: 100%;
  text-align: center;
  font-size: 32px;
  color: #fff;
  font-weight: bold;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 200px;
  width: 100%;

  ${handyBreakpoint} {
    padding: 16px;
    margin-top: 64px;
    box-sizing: border-box;
  }
`;

const Subtitle = styled.div`
  font-size: 25px;
  color: #fff;
  font-weight: 300;
`;

const PositionedButton = styled(Button)`
  margin: 20px auto;
  border-radius: 2px;
  width: 300px;
  font-size: 16px;
`;

export const Tabs = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  background: #a7a7a7;
  border-radius: 40px;
  height: 64px;
  width: 480px;
`;

export const Tab = styled.div<{
  active?: boolean;
}>`
  width: 100%;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.active ? '#000' : '#fff')};
  background-color: ${(props) => (props.active ? '#fff' : '')};
  padding: 8px 12px;
  margin: 8px;
  border-radius: 40px;
  text-align: center;
  transition: all 0.1s ease-in;
  line-height: 1.8;
`;

export enum TAB_TYPES {
  SWIPE = 'SWIPE',
  MANUAL = 'MANUAL',
  QR = 'QR',
}

const REFETCH_BALANCE = gql`
  mutation RefreshGiftcard($universeId: String!, $shortcode: String!) {
    refreshGiftcard(universeId: $universeId, shortcode: $shortcode) {
      giftcard {
        id
        balance
      }
      error
      success
    }
  }
`;

interface Props {
  id: string;
  amount: number;
  isFailing: boolean;
  cardNumberFormat?: string;
  cardNumberMinLength?: number;
  availableTabTypes?: TAB_TYPES[];
  getAvailableBalance: (cardNumber: string) => Promise<number>;
  makePurchase: (
    cardNumber: string,
    amount: number,
  ) => Promise<{ success: boolean; message?: string; transactionId?: string }>;
  onComplete: (transactionId?: string) => void;
  onFail: (reason?: string) => void;
  onCancel?: () => void;
}

type PaymentState =
  | { status: 'idle' }
  | { status: 'display_saldo' }
  | { status: 'pending' }
  | { status: 'success' }
  | { status: 'error'; error: string };

const GiftCardPayment: React.FunctionComponent<Props> = ({
  id,
  amount,
  isFailing,
  cardNumberFormat,
  cardNumberMinLength = 5,
  availableTabTypes = [TAB_TYPES.SWIPE, TAB_TYPES.MANUAL, TAB_TYPES.QR],
  getAvailableBalance,
  makePurchase,
  onComplete,
  onFail,
  onCancel,
}) => {
  const { i18n } = useI18n();
  const [paymentState, setPaymentState] = useState<PaymentState>({ status: 'idle' });
  const [inputIsInvalid, setInputIsInvalid] = useState(false);
  const [availableBalance, setAvailableBalance] = useState<null | number>(null);
  const [cardNumber, setCardNumber] = useState<string>('');
  const [tabType, setTabType] = useState(availableTabTypes[0]);
  const universeId = useGiftCardsContext().universeId;
  const currency = useCurrency();
  const [refetchBalance] = useMutation<RefreshGiftcard, RefreshGiftcardVariables>(
    REFETCH_BALANCE,
    {
      variables: {
        universeId,
        shortcode: cardNumber,
      },
      onCompleted: (data) => {
        if (data.refreshGiftcard.giftcard) {
          setAvailableBalance(data.refreshGiftcard.giftcard.balance);
        }
      },
    },
  );

  function handleCompleted(transactionId?: string) {
    setPaymentState({ status: 'success' });

    setTimeout(() => {
      onComplete(transactionId);
    }, 1000);
  }

  // Will trigger visual loading until payment resolves
  function handlePayment() {
    setPaymentState({ status: 'pending' });
    // Check if the card has the available funds
    if (availableBalance! < amount) {
      setPaymentState({
        status: 'error',
        error: 'Insufficient funds',
      });
      return;
    }

    makePurchase(cardNumber, amount)
      .then(({ success, transactionId }) => {
        if (success) {
          handleCompleted(transactionId);
        } else {
          setPaymentState({
            status: 'error',
            error: 'Could not complete transaction',
          });
        }
      })
      .catch((error) => {
        setPaymentState({
          status: 'error',
          error: error.message,
        });
      });
  }

  const fullCardNumber = useDebounce(cardNumber);

  // Try to get available balance on card number change
  useEffect(() => {
    if (fullCardNumber.length === 0) {
      return;
    }

    if (fullCardNumber.length < cardNumberMinLength) {
      setInputIsInvalid(true);
      return;
    }

    getAvailableBalance(fullCardNumber)
      .then((balance) => {
        refetchBalance();

        if (typeof balance !== 'number') {
          throw new Error('Balance recieved was faulty, check card.');
        }
        setAvailableBalance(balance);
        setPaymentState({ status: 'display_saldo' });
      })
      .catch((err) => {
        setPaymentState({ status: 'error', error: err.message });
      });
  }, [fullCardNumber]);

  if (isFailing) {
    return (
      <Wrapper className="background">
        <AbsoluteWrapper>{i18n('GC.Canceled')}</AbsoluteWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      className="background"
      onTouchStart={(e) => {
        if (
          paymentState.status === 'idle' &&
          (e.target as HTMLDivElement).className.includes('background')
        ) {
          onFail && onFail();
        }
      }}>
      {paymentState.status === 'success' && (
        <AbsoluteWrapper>Payment {i18n('GC.Completed')}</AbsoluteWrapper>
      )}
      {inputIsInvalid && (
        <AbsoluteWrapper>
          {i18n('GC.InvalidInput')}
          <Subtitle>{i18n('GC.TryAgain')}</Subtitle>
          <Subtitle>
            {i18n('GC.InputWas')} {fullCardNumber}
          </Subtitle>
          <PositionedButton
            padding="large"
            onTouchStart={() => {
              setCardNumber('');
              setInputIsInvalid(false);
            }}>
            {i18n('GC.OK')}
          </PositionedButton>
        </AbsoluteWrapper>
      )}
      {paymentState.status === 'display_saldo' && (
        <AbsoluteWrapper>
          {/* Non-null assertion due to this state depending on balance beeing available */}
          {currency.format(availableBalance!)} <Subtitle>{i18n('GC.AvailableBalance')}</Subtitle>
          <PositionedButton
            backgroundColor="colorBlueGray50"
            onTouchStart={handlePayment}
            padding="large">
            {i18n('General.Proceed')}
          </PositionedButton>
          <PositionedButton
            backgroundColor="colorBlueGray1300"
            color="white"
            padding="large"
            onTouchEnd={() => onFail('Manually cancelled')}>
            {i18n('General.Cancel')}
          </PositionedButton>
        </AbsoluteWrapper>
      )}
      {paymentState.status === 'error' && (
        <AbsoluteWrapper>
          {i18n('GC.Wrong')}
          <Subtitle>{paymentState.error}</Subtitle>
          <PositionedButton padding="large" onTouchStart={() => onFail(paymentState.error)}>
            {i18n('GC.OK')}
          </PositionedButton>
        </AbsoluteWrapper>
      )}
      {cardNumber !== fullCardNumber ||
        (paymentState.status === 'pending' && (
          <AbsoluteWrapper>
            {i18n('GC.Loading')}
            <Subtitle>{i18n('GC.CheckingPayment')}</Subtitle>
          </AbsoluteWrapper>
        ))}
      {paymentState.status === 'idle' && (
        <InputWrapper>
          <Tabs>
            {availableTabTypes.map((tab) => (
              <Tab key={tab} active={tabType === tab} onTouchStart={() => setTabType(tab)}>
                {i18n(`GC.${tab}`)}
              </Tab>
            ))}
          </Tabs>

          {tabType === TAB_TYPES.SWIPE && (
            <SwipeView cardNumber={cardNumber} setCardNumber={setCardNumber} />
          )}
          {tabType === TAB_TYPES.MANUAL && (
            <ManualView submit={setCardNumber} format={cardNumberFormat} />
          )}
          {tabType === TAB_TYPES.QR && <QRScanView submit={setCardNumber} />}
        </InputWrapper>
      )}
    </Wrapper>
  );
};

export default GiftCardPayment;
